import apiClient from '../../apis';

/** One typed source behind a driver (#185), as it rides a feed card's evidence. */
export interface FeedEvidenceSource {
  type: string;
  weak: boolean;
}

/** One WHY line on a post: the driver's label, the tier it earned, and its sources. */
export interface FeedEvidence {
  label: string;
  tier: string;
  sources: FeedEvidenceSource[];
}

/** The agent's proposal (WHAT NEXT), when it made one — propose only (#199). */
export interface FeedProposal {
  text?: string;
  tier?: string;
}

/** A human sign-off on a proposal (#201): who, and when. It executes nothing. */
export interface FeedApproved {
  by: string;
  by_name: string;
  at: string;
}

/** One contest and the reason it carried — a contest needs a reason. */
export interface FeedContestReason {
  by: string;
  reason: string;
}

/**
 * One agent post (#201): a WHAT/WHY/NEXT card from an agent's run, already
 * disclosed for the viewer's evidence policy on the server — evidence below
 * the bar is dropped, and a facts-only viewer sees no summary or proposal.
 */
export interface FeedCard {
  id: string;
  agent_id: string;
  run_id: string;
  tier: 'confirmed' | 'emerging' | 'unconfirmed';
  headline: string;
  summary: string | null;
  proposal: FeedProposal | null;
  evidence: FeedEvidence[];
  session_id: string | null;
  approved: FeedApproved | null;
  created_at: string;
  agent_name: string | null;
  seat_holder: string | null;
  reach: number | null;
  stats: { confirms: number; contests: number; likes: number };
  you: { confirmed: boolean; contested: boolean; liked: boolean; approved: boolean };
  contest_reasons: FeedContestReason[];
}

/** The feed the viewer sees, and what their bar hid below it. */
export interface Feed {
  posts: FeedCard[];
  hidden_count: number;
  policy: 'gated' | 'full' | 'facts';
  bar: string | null;
  raise_to: string | null;
}

/** A signed reaction the server wrote (confirm · contest · like · approve). */
export interface Reaction {
  id?: string;
  post_id?: string;
  kind?: string;
  reason?: string | null;
  provenance?: Record<string, unknown>;
  [k: string]: unknown;
}

const BASE = '/api/feed';

export async function getFeed(limit = 100): Promise<Feed> {
  const { data } = await apiClient.get<Feed>(`${BASE}/`, { params: { limit } });
  return data;
}

export async function confirmPost(id: string): Promise<Reaction> {
  const { data } = await apiClient.post<{ reaction: Reaction }>(`${BASE}/posts/${id}/confirm`);
  return data.reaction;
}

export async function likePost(id: string): Promise<Reaction> {
  const { data } = await apiClient.post<{ reaction: Reaction }>(`${BASE}/posts/${id}/like`);
  return data.reaction;
}

/** A contest needs a reason; the server 400s without one. Carries the agent's new authoritative reach. */
export async function contestPost(id: string, reason: string): Promise<{ reaction: Reaction; agent_id: string; reach: number | null }> {
  const { data } = await apiClient.post<{ reaction: Reaction; agent_id: string; reach: number | null }>(`${BASE}/posts/${id}/contest`, { reason });
  return data;
}

/** Sign off on a proposal (409 when the post has none). Executes nothing. Carries the agent's new authoritative reach. */
export async function approvePost(id: string): Promise<{ post_id: string; approved: FeedApproved | null; agent_id: string; reach: number | null }> {
  const { data } = await apiClient.post<{ post_id: string; approved: FeedApproved | null; agent_id: string; reach: number | null }>(`${BASE}/posts/${id}/approve`);
  return data;
}

/** A comment IS the agent's steering (#202): signed, on its wall, read before its next run. */
export async function commentPost(id: string, text: string, opId?: string): Promise<{ comment: unknown; activity: unknown }> {
  const { data } = await apiClient.post<{ comment: unknown; activity: unknown }>(
    `${BASE}/posts/${id}/comment`,
    { text, ...(opId ? { op_id: opId } : {}) },
  );
  return data;
}

/** The server's error string for a failed call, else the transport's. */
export function feedErrorMessage(e: any, fallback = 'Request failed'): string {
  return e?.response?.data?.error || e?.message || fallback;
}
